import { Ajuda } from '../../design/Ajuda';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { hojeBrasilia, lerGiroMax, lerGiroMaxDoProduto } from '../../central';
import type {
  GiroMax,
  GrupoPorTurno,
  LinhaDoGiroMax,
  ProdutoDoGiroMax,
  ProdutoDoGiroMaxDetalhe,
  TurnoDoGiroMax,
} from '../../central';
import { ErroDeArea } from '../../design/Estados';
import { baixarCsv } from '../../design/csv';
import { Icone } from '../../design/Icone';
import { fmt } from '../../design/graficos/modelos';
import { SubnavDoGiro } from './SubnavDoGiro';

/**
 * GIRO MAX (dono, 27/09/2026) — quais produtos venderam MAIS DE N unidades num único dia, e em
 * que dia. Em cima o resumo e os produtos; embaixo a lista completa (data, produto, cupom,
 * vendedor, quantidade) de cada dia que passou do limite. Clicar no produto abre a gaveta: dia que
 * mais vendeu, dia que menos vendeu, quem mais vendeu, o dia a dia e os cupons.
 *
 * TURNO (hora de Brasília): manhã 06h–14h59, tarde/noite 15h–23h59. O quadro "SKUs por turno e
 * grupo" compara os turnos lado a lado e não depende do limite nem do turno escolhido.
 *
 * É dado da LOJA (vendas por vendedor): a API exige `loja.ver` — sem ela, a tela diz que é área
 * dos donos.
 */

const PERIODOS: readonly [number, string][] = [
  [7, '7 dias'],
  [15, '15 dias'],
  [30, '30 dias'],
  [60, '60 dias'],
  [90, '90 dias'],
];
const POR_PAGINA = 200;
const PRODUTOS_POR_VEZ = 30;
const TURNOS: readonly [TurnoDoGiroMax | null, string][] = [
  [null, 'Dia todo'],
  ['manha', '06h–15h'],
  ['tarde', '15h–00h'],
];
const NOME_DO_TURNO: Record<TurnoDoGiroMax, string> = {
  manha: 'manhã (06h–15h)',
  tarde: 'tarde/noite (15h–00h)',
};

const mais = (d: string, n: number) =>
  new Date(Date.parse(`${d}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
const dataBr = (d: string) => d.split('-').reverse().join('/');
const diaCurto = (d: string) => d.split('-').reverse().slice(0, 2).join('/');
const diaDaSemana = (d: string) =>
  new Date(`${d}T12:00:00Z`).toLocaleDateString('pt-BR', { weekday: 'short', timeZone: 'UTC' });
const un = (n: number) => `${fmt(n, 'inteiro')} un.`;

export function GiroMaxTela() {
  const hoje = hojeBrasilia();
  // o dia de hoje ainda está entrando: o padrão termina ontem
  const [ate, setAte] = useState(mais(hoje, -1));
  const [de, setDe] = useState(mais(hoje, -30));
  const [acimaDe, setAcimaDe] = useState(6);
  const [textoAcima, setTextoAcima] = useState('6');
  const [busca, setBusca] = useState('');
  const [texto, setTexto] = useState('');
  const [fabricante, setFabricante] = useState('');
  const [vendedor, setVendedor] = useState('');
  const [comTaxas, setComTaxas] = useState(false);
  const [turno, setTurno] = useState<TurnoDoGiroMax | null>(null);
  const [d, setD] = useState<GiroMax | null>(null);
  const [erro, setErro] = useState<'sem-acesso' | 'falha' | null>(null);
  const [aberto, setAberto] = useState<ProdutoDoGiroMax | null>(null);
  const [soDoProduto, setSoDoProduto] = useState<string | null>(null);
  const [pagina, setPagina] = useState(1);
  const [verProdutos, setVerProdutos] = useState(PRODUTOS_POR_VEZ);

  const carregar = useCallback(() => {
    setErro(null);
    setD(null);
    setPagina(1);
    setVerProdutos(PRODUTOS_POR_VEZ);
    void lerGiroMax({
      de,
      ate,
      acimaDe,
      busca: busca || null,
      fabricante: fabricante || null,
      vendedor: vendedor || null,
      comTaxas,
      turno,
    }).then((r) => {
      if (r.ok) return setD(r.dados);
      setErro(r.falha.tipo === 'sem-acesso' ? 'sem-acesso' : 'falha');
    });
  }, [de, ate, acimaDe, busca, fabricante, vendedor, comTaxas, turno]);
  useEffect(carregar, [carregar]);

  const diasNoPeriodo = Math.round((Date.parse(ate) - Date.parse(de)) / 86_400_000) + 1;
  const periodoRapido = PERIODOS.find(
    ([n]) => de === mais(ate, -(n - 1)) && ate === mais(hoje, -1),
  );
  const escolherPeriodo = (n: number) => {
    setAte(mais(hoje, -1));
    setDe(mais(hoje, -n));
  };
  const aplicarAcima = () => {
    const n = Math.floor(Number(textoAcima.replace(',', '.')));
    if (Number.isFinite(n) && n >= 0 && n <= 100_000) setAcimaDe(n);
    else setTextoAcima(String(acimaDe));
  };

  const linhas = useMemo(
    () => (d ? (soDoProduto ? d.linhas.filter((l) => l.productId === soDoProduto) : d.linhas) : []),
    [d, soDoProduto],
  );
  const produtoFiltrado = soDoProduto
    ? d?.produtos.find((p) => p.productId === soDoProduto)
    : undefined;
  const visiveis = linhas.slice(0, pagina * POR_PAGINA);

  const baixarProdutos = () =>
    d &&
    baixarCsv(`giro-max-produtos-${de}-a-${ate}-acima-de-${String(acimaDe)}.csv`, [
      [
        'Código',
        'Produto',
        'Fabricante',
        `Dias acima de ${String(acimaDe)}`,
        'Maior dia',
        'Qtd no maior dia',
        'Total no período',
      ],
      ...d.produtos.map((p) => [
        p.codigo,
        p.nome,
        p.fabricante,
        p.diasAcima,
        dataBr(p.maiorDia.dia),
        p.maiorDia.qtd,
        p.unidadesNoPeriodo,
      ]),
    ]);
  const baixarLista = () =>
    baixarCsv(`giro-max-cupons-${de}-a-${ate}-acima-de-${String(acimaDe)}.csv`, [
      ['Data', 'Código', 'Produto', 'Cupom', 'Vendedor', 'Quantidade'],
      ...linhas.map((l) => [dataBr(l.dia), l.codigo, l.produto, l.cupom, l.vendedor, l.qtd]),
    ]);

  return (
    <section className="cv gr">
      <SubnavDoGiro atual="giro-max" direita="vendas do HOS · sem canceladas" />
      <div className="cv-corpo gm">
        <header className="gm-topo">
          <div>
            <h1>
              Giro Max
              <Ajuda rotulo="o Giro Max">
                Mostra os produtos que tiveram um dia de venda forte (acima do número escolhido).
                Serve para saber quanto precisa caber na prateleira para não faltar num dia bom. No
                alvo automático, o Giro usa o maior dia de cada produto como alvo.
              </Ajuda>
            </h1>
            <p className="cv-cinza">
              Produtos que venderam <b>mais de {acimaDe}</b> unidades num único dia
              {turno ? (
                <>
                  {' '}
                  na <b>{NOME_DO_TURNO[turno]}</b>
                </>
              ) : null}{' '}
              · {dataBr(de)} a {dataBr(ate)} ({diasNoPeriodo} dias)
            </p>
          </div>
          {d && d.produtos.length > 0 && (
            <button type="button" className="cv-botao" onClick={baixarProdutos}>
              <Icone nome="receipt" tamanho={14} traco={1.7} /> Baixar produtos (Excel)
            </button>
          )}
        </header>

        <div className="gm-filtros">
          <div className="cv-segmento" role="tablist" aria-label="Período">
            {PERIODOS.map(([n, rotulo]) => (
              <button
                key={n}
                type="button"
                role="tab"
                aria-selected={periodoRapido?.[0] === n}
                className={periodoRapido?.[0] === n ? 'cv-sel' : ''}
                onClick={() => escolherPeriodo(n)}
              >
                {rotulo}
              </button>
            ))}
          </div>
          <label className="cv-select">
            De
            <input
              type="date"
              value={de}
              max={ate}
              onChange={(e) => e.target.value && setDe(e.target.value)}
            />
          </label>
          <label className="cv-select">
            Até
            <input
              type="date"
              value={ate}
              min={de}
              max={hoje}
              onChange={(e) => e.target.value && setAte(e.target.value)}
            />
          </label>
          <span className="gm-com-ajuda">
            <div className="cv-segmento" role="tablist" aria-label="Turno">
              {TURNOS.map(([t, rotulo]) => (
                <button
                  key={rotulo}
                  type="button"
                  role="tab"
                  aria-selected={turno === t}
                  className={turno === t ? 'cv-sel' : ''}
                  onClick={() => setTurno(t)}
                >
                  {rotulo}
                </button>
              ))}
            </div>
            <Ajuda rotulo="o turno">
              Conta só as vendas daquele horário: 06h–15h (manhã) ou 15h–00h (tarde e noite). Dia
              todo = as duas juntas.
            </Ajuda>
          </span>
          <label className="cv-select gm-acima">
            Mais de
            <input
              inputMode="numeric"
              value={textoAcima}
              aria-label="Vendeu mais de quantas unidades no mesmo dia"
              onChange={(e) => setTextoAcima(e.target.value.replace(/\D/g, ''))}
              onBlur={aplicarAcima}
              onKeyDown={(e) => e.key === 'Enter' && aplicarAcima()}
            />
            un./dia
            <Ajuda rotulo="o mínimo por dia">
              Só aparecem produtos que, em algum dia do período, venderam mais do que este número de
              unidades.
            </Ajuda>
          </label>
          <label className="cv-select">
            <Icone nome="users" tamanho={14} traco={1.6} />
            <select
              value={vendedor}
              aria-label="Vendedor"
              onChange={(e) => setVendedor(e.target.value)}
            >
              <option value="">Todos os vendedores</option>
              {(d?.vendedores ?? []).map((v) => (
                <option key={v.codigo} value={v.codigo}>
                  {v.nome} · {v.codigo}
                </option>
              ))}
            </select>
          </label>
          <label className="cv-select">
            Fabricante
            <select value={fabricante} onChange={(e) => setFabricante(e.target.value)}>
              <option value="">Todos</option>
              {(d?.fabricantes ?? []).map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </label>
          <form
            className="cv-busca-nova"
            onSubmit={(e) => {
              e.preventDefault();
              setBusca(texto.trim());
            }}
          >
            <Icone nome="search" tamanho={15} traco={1.7} />
            <input
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="Produto, código ou EAN"
              aria-label="Buscar produto, código ou EAN"
            />
            {busca && (
              <button
                type="button"
                aria-label="Limpar busca"
                onClick={() => {
                  setTexto('');
                  setBusca('');
                }}
              >
                <Icone nome="x" tamanho={13} traco={1.8} />
              </button>
            )}
          </form>
          <label className="gm-check">
            <input
              type="checkbox"
              checked={comTaxas}
              onChange={(e) => setComTaxas(e.target.checked)}
            />
            Incluir taxas e entrega
            <Ajuda rotulo="incluir taxas e entrega">
              Desligado, itens que não são produto de prateleira (taxa de entrega, serviços) ficam
              fora da conta.
            </Ajuda>
          </label>
        </div>

        {erro === 'sem-acesso' ? (
          <div className="cv-aviso">
            <Icone nome="lock" tamanho={14} traco={1.7} />
            Giro Max mostra vendas da loja por vendedor e cupom: é área dos donos.
          </div>
        ) : erro ? (
          <ErroDeArea oQue="o Giro Max" aoTentar={carregar} />
        ) : !d ? (
          <div className="cv-carregando" aria-busy="true" />
        ) : (
          <>
            <div className="cv-kpis gm-kpis">
              <Numero
                rotulo={`PRODUTOS ACIMA DE ${String(acimaDe)}`}
                valor={fmt(d.resumo.produtos, 'inteiro')}
                sub="venderam mais que isso em pelo menos 1 dia"
                destaque
              />
              <Numero
                rotulo="VEZES (PRODUTO × DIA)"
                valor={fmt(d.resumo.ocorrencias, 'inteiro')}
                sub={
                  d.resumo.produtos
                    ? `média de ${fmt(d.resumo.ocorrencias / d.resumo.produtos, 'decimal')} dias por produto`
                    : '—'
                }
              />
              <Numero
                rotulo="UNIDADES NESSES DIAS"
                valor={fmt(d.resumo.unidades, 'inteiro')}
                sub="só os dias que passaram do limite"
              />
              <Numero
                rotulo="DIA COM MAIS PRODUTOS"
                valor={d.resumo.diaComMaisProdutos ? dataBr(d.resumo.diaComMaisProdutos.dia) : '—'}
                sub={
                  d.resumo.diaComMaisProdutos
                    ? `${diaDaSemana(d.resumo.diaComMaisProdutos.dia)} · ${fmt(d.resumo.diaComMaisProdutos.produtos, 'inteiro')} produtos acima`
                    : 'nenhum dia passou do limite'
                }
              />
            </div>

            <QuadroDeTurnos grupos={d.porTurnoEGrupo} />

            <div className="cv-cartao cv-sem-pad">
              <div className="cv-cartao-topo cv-pad">
                <h2>Produtos · {fmt(d.produtos.length, 'inteiro')}</h2>
                <span className="cv-cinza cv-peq">
                  clique no produto para ver o dia que mais vendeu, o que menos vendeu e quem vendeu
                </span>
              </div>
              {d.produtos.length === 0 ? (
                <p className="cv-pad cv-cinza">
                  Nenhum produto vendeu mais de {acimaDe} unidades num único dia nesse período e
                  filtro.
                </p>
              ) : (
                <div className="cv-rolagem">
                  <table className="cv-tabela gm-tabela">
                    <thead>
                      <tr>
                        <th>Produto</th>
                        <th
                          className="cv-dir"
                          title={`Dias em que vendeu mais de ${String(acimaDe)}`}
                        >
                          Dias acima
                        </th>
                        <th>Maior dia</th>
                        <th className="cv-dir">Qtd no maior dia</th>
                        <th className="cv-dir">Total no período</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {d.produtos.slice(0, verProdutos).map((p) => (
                        <tr
                          key={p.productId}
                          className="gm-clicavel"
                          tabIndex={0}
                          onClick={() => setAberto(p)}
                          onKeyDown={(e) => e.key === 'Enter' && setAberto(p)}
                        >
                          <td>
                            <div className="cv-forte">{p.nome}</div>
                            <div className="cv-mono cv-cinza cv-peq">
                              {p.codigo ?? '—'}
                              {p.fabricante && ` · ${p.fabricante}`}
                            </div>
                          </td>
                          <td className="cv-dir">
                            <span className="gm-dias">
                              {p.diasAcima}
                              <i
                                style={{
                                  width: `${String(Math.min(100, (p.diasAcima / diasNoPeriodo) * 100))}%`,
                                }}
                              />
                            </span>
                          </td>
                          <td>
                            {dataBr(p.maiorDia.dia)}{' '}
                            <span className="cv-cinza cv-peq">{diaDaSemana(p.maiorDia.dia)}</span>
                          </td>
                          <td className="cv-dir cv-forte">{fmt(p.maiorDia.qtd, 'inteiro')}</td>
                          <td className="cv-dir">{fmt(p.unidadesNoPeriodo, 'inteiro')}</td>
                          <td className="cv-dir">
                            <button
                              type="button"
                              className="gm-link"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSoDoProduto(p.productId);
                                setPagina(1);
                                document
                                  .getElementById('gm-lista')
                                  ?.scrollIntoView({ behavior: 'smooth' });
                              }}
                            >
                              ver cupons
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {d.produtos.length > verProdutos && (
                <div className="cv-pad gm-mais">
                  <button
                    type="button"
                    className="cv-botao"
                    onClick={() => setVerProdutos((n) => n + PRODUTOS_POR_VEZ)}
                  >
                    Ver mais{' '}
                    {fmt(Math.min(PRODUTOS_POR_VEZ, d.produtos.length - verProdutos), 'inteiro')}
                  </button>
                  <button
                    type="button"
                    className="cv-botao"
                    onClick={() => setVerProdutos(d.produtos.length)}
                  >
                    Ver todos ({fmt(d.produtos.length, 'inteiro')})
                  </button>
                  <span className="cv-cinza cv-peq">
                    {fmt(verProdutos, 'inteiro')} de {fmt(d.produtos.length, 'inteiro')} na tela
                  </span>
                </div>
              )}
              {verProdutos > PRODUTOS_POR_VEZ && d.produtos.length <= verProdutos && (
                <div className="cv-pad gm-mais">
                  <button
                    type="button"
                    className="cv-botao"
                    onClick={() => setVerProdutos(PRODUTOS_POR_VEZ)}
                  >
                    Ver menos
                  </button>
                </div>
              )}
            </div>

            <div className="cv-cartao cv-sem-pad" id="gm-lista">
              <div className="cv-cartao-topo cv-pad">
                <h2>
                  Lista completa · {fmt(linhas.length, 'inteiro')} cupons
                  {produtoFiltrado && (
                    <span className="cv-cinza gr-normal"> · {produtoFiltrado.nome}</span>
                  )}
                </h2>
                <span className="gm-acoes">
                  {soDoProduto && (
                    <button type="button" className="cv-botao" onClick={() => setSoDoProduto(null)}>
                      <Icone nome="x" tamanho={13} traco={1.8} /> Todos os produtos
                    </button>
                  )}
                  {linhas.length > 0 && (
                    <button type="button" className="cv-botao" onClick={baixarLista}>
                      <Icone nome="receipt" tamanho={14} traco={1.7} /> Baixar lista (Excel)
                    </button>
                  )}
                </span>
              </div>
              {d.linhasCortadas && (
                <div className="cv-aviso gm-aviso">
                  <Icone nome="warn" tamanho={14} traco={1.7} />A lista passou de 5.000 cupons e foi
                  cortada. Diminua o período ou filtre por produto, fabricante ou vendedor. Os
                  produtos e os números de cima estão completos.
                </div>
              )}
              {linhas.length === 0 ? (
                <p className="cv-pad cv-cinza">Sem cupons para mostrar.</p>
              ) : (
                <>
                  <div className="cv-rolagem">
                    <table data-sem-ordenar className="cv-tabela gm-tabela">
                      <thead>
                        <tr>
                          <th>Data</th>
                          <th>Produto</th>
                          <th>Cupom</th>
                          <th>Vendedor</th>
                          <th className="cv-dir">Quantidade</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visiveis.map((l, k) => (
                          <LinhaDaLista
                            key={`${l.saleId}-${l.productId}-${String(k)}`}
                            l={l}
                            anterior={visiveis[k - 1]}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {linhas.length > visiveis.length && (
                    <div className="cv-pad gm-mais">
                      <button
                        type="button"
                        className="cv-botao"
                        onClick={() => setPagina((n) => n + 1)}
                      >
                        Mostrar mais{' '}
                        {fmt(Math.min(POR_PAGINA, linhas.length - visiveis.length), 'inteiro')}{' '}
                        <span className="cv-cinza">
                          ({fmt(visiveis.length, 'inteiro')} de {fmt(linhas.length, 'inteiro')})
                        </span>
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
      {aberto && (
        <GavetaDoProduto
          produto={aberto}
          filtro={{ de, ate, acimaDe, vendedor: vendedor || null, turno }}
          aoFechar={() => setAberto(null)}
        />
      )}
    </section>
  );
}

/** SKUs distintos vendidos em cada turno, por grupo — lado a lado, com o total da loja. */
function QuadroDeTurnos({ grupos }: { grupos: readonly GrupoPorTurno[] }) {
  if (!grupos.length) return null;
  const soma = (f: (g: GrupoPorTurno) => number) => grupos.reduce((a, g) => a + f(g), 0);
  const total = {
    skus: soma((g) => g.skus),
    m: soma((g) => g.manha.skus),
    t: soma((g) => g.tarde.skus),
    d: soma((g) => g.madrugada.skus),
    um: soma((g) => g.manha.unidades),
    ut: soma((g) => g.tarde.unidades),
  };
  const comMadrugada = total.d > 0;
  const barra = (m: number, t: number) => {
    const s = m + t || 1;
    return (
      <span className="gm-turno-barra" title={`manhã ${String(m)} · tarde ${String(t)}`}>
        <i className="gm-turno-m" style={{ width: `${String((m / s) * 100)}%` }} />
        <i className="gm-turno-t" style={{ width: `${String((t / s) * 100)}%` }} />
      </span>
    );
  };
  return (
    <div className="cv-cartao cv-sem-pad">
      <div className="cv-cartao-topo cv-pad">
        <h2>
          SKUs vendidos por turno e grupo
          <Ajuda rotulo="os SKUs por turno">
            Quantos produtos diferentes cada grupo vendeu em cada turno. Ajuda a decidir quantos
            produtos cabem no Giro de cada horário.
          </Ajuda>
        </h2>
        <span className="cv-cinza cv-peq">
          produtos diferentes vendidos em cada turno · um SKU que vendeu nos dois turnos conta nos
          dois
        </span>
      </div>
      <div className="cv-rolagem">
        <table className="cv-tabela gm-tabela">
          <thead>
            <tr>
              <th>Grupo</th>
              <th className="cv-dir">SKUs 06h–15h</th>
              <th className="cv-dir">SKUs 15h–00h</th>
              {comMadrugada && <th className="cv-dir">SKUs 00h–06h</th>}
              <th className="cv-dir">SKUs no dia</th>
              <th>Manhã × tarde</th>
              <th className="cv-dir">Unid. 06h–15h</th>
              <th className="cv-dir">Unid. 15h–00h</th>
            </tr>
          </thead>
          <tbody>
            {grupos.map((g) => (
              <tr key={g.grupo}>
                <td className="cv-forte">{g.grupo}</td>
                <td className="cv-dir cv-forte">{fmt(g.manha.skus, 'inteiro')}</td>
                <td className="cv-dir cv-forte">{fmt(g.tarde.skus, 'inteiro')}</td>
                {comMadrugada && <td className="cv-dir">{fmt(g.madrugada.skus, 'inteiro')}</td>}
                <td className="cv-dir">{fmt(g.skus, 'inteiro')}</td>
                <td>{barra(g.manha.skus, g.tarde.skus)}</td>
                <td className="cv-dir">{fmt(g.manha.unidades, 'inteiro')}</td>
                <td className="cv-dir">{fmt(g.tarde.unidades, 'inteiro')}</td>
              </tr>
            ))}
            <tr className="gm-total">
              <td>Total da loja</td>
              <td className="cv-dir">{fmt(total.m, 'inteiro')}</td>
              <td className="cv-dir">{fmt(total.t, 'inteiro')}</td>
              {comMadrugada && <td className="cv-dir">{fmt(total.d, 'inteiro')}</td>}
              <td className="cv-dir">{fmt(total.skus, 'inteiro')}</td>
              <td>{barra(total.m, total.t)}</td>
              <td className="cv-dir">{fmt(total.um, 'inteiro')}</td>
              <td className="cv-dir">{fmt(total.ut, 'inteiro')}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Numero({
  rotulo,
  valor,
  sub,
  destaque,
}: {
  rotulo: string;
  valor: string;
  sub: string;
  destaque?: boolean;
}) {
  return (
    <div className={`cv-kpi ${destaque ? 'gm-kpi-destaque' : ''}`}>
      <div className="cv-kpi-rotulo">{rotulo}</div>
      <div className="cv-kpi-valor">{valor}</div>
      <div className="cv-cinza cv-peq">{sub}</div>
    </div>
  );
}

function LinhaDaLista({
  l,
  anterior,
}: {
  l: LinhaDoGiroMax;
  anterior: LinhaDoGiroMax | undefined;
}) {
  const novoGrupo = !anterior || anterior.dia !== l.dia || anterior.productId !== l.productId;
  return (
    <tr className={novoGrupo ? 'gm-grupo' : ''}>
      <td className="cv-mono">{novoGrupo ? dataBr(l.dia) : ''}</td>
      <td>
        {novoGrupo ? (
          <>
            <div className="cv-forte">{l.produto}</div>
            <div className="cv-mono cv-cinza cv-peq">{l.codigo ?? '—'}</div>
          </>
        ) : (
          <span className="cv-cinza cv-peq">〃</span>
        )}
      </td>
      <td>
        <a className="cv-mono" href={`#/cupom?id=${l.saleId}`}>
          {l.cupom}
        </a>
      </td>
      <td>{l.vendedor ?? <span className="cv-cinza">sem vendedor</span>}</td>
      <td className="cv-dir cv-forte">{fmt(l.qtd, 'inteiro')}</td>
    </tr>
  );
}

function GavetaDoProduto({
  produto,
  filtro,
  aoFechar,
}: {
  produto: ProdutoDoGiroMax;
  filtro: {
    de: string;
    ate: string;
    acimaDe: number;
    vendedor: string | null;
    turno: TurnoDoGiroMax | null;
  };
  aoFechar: () => void;
}) {
  const [d, setD] = useState<ProdutoDoGiroMaxDetalhe | null>(null);
  const [erro, setErro] = useState(false);
  const carregar = useCallback(() => {
    setErro(false);
    setD(null);
    void lerGiroMaxDoProduto(produto.productId, filtro).then((r) =>
      r.ok ? setD(r.dados) : setErro(true),
    );
    // o filtro é fotografado ao abrir a gaveta: só o produto recarrega
  }, [produto.productId]);
  useEffect(carregar, [carregar]);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && aoFechar();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [aoFechar]);

  const maxVend = Math.max(1, ...(d?.vendedores.map((v) => v.qtd) ?? [1]));
  const maxDia = Math.max(1, ...(d?.dias.map((x) => x.qtd) ?? [1]));
  return (
    <div className="cv-veu" onClick={aoFechar}>
      <aside
        className="cv-gaveta"
        role="dialog"
        aria-label={`Giro Max · ${produto.nome}`}
        onClick={(e) => e.stopPropagation()}
      >
        <header>
          <div className="cv-gaveta-selos">
            <span className="gr-tag">GIRO MAX</span>
            <span className="cv-cinza cv-peq">
              {dataBr(filtro.de)} a {dataBr(filtro.ate)} · mais de {filtro.acimaDe} un./dia
              {filtro.turno ? ` · ${NOME_DO_TURNO[filtro.turno]}` : ''}
            </span>
          </div>
          <button type="button" className="cv-fechar" aria-label="Fechar" onClick={aoFechar}>
            <Icone nome="x" tamanho={15} traco={1.8} />
          </button>
          <h2>{produto.nome}</h2>
          <p>
            {produto.codigo ?? 'sem código'}
            {produto.fabricante ? ` · ${produto.fabricante}` : ''}
          </p>
        </header>
        <div className="cv-gaveta-corpo">
          {erro ? (
            <ErroDeArea oQue="o produto" aoTentar={carregar} />
          ) : !d ? (
            <div className="cv-carregando" aria-busy="true" />
          ) : (
            <>
              <div className="gm-gaveta-numeros">
                <div className="gm-bloco gm-bloco-verde">
                  <span>DIA QUE MAIS VENDEU</span>
                  <b>{d.maiorDia ? dataBr(d.maiorDia.dia) : '—'}</b>
                  <em>
                    {d.maiorDia
                      ? `${diaDaSemana(d.maiorDia.dia)} · ${un(d.maiorDia.qtd)} em ${fmt(d.maiorDia.cupons, 'inteiro')} cupons`
                      : 'sem venda'}
                  </em>
                </div>
                <div className="gm-bloco">
                  <span>DIA QUE MENOS VENDEU</span>
                  <b>{d.menorDia ? dataBr(d.menorDia.dia) : '—'}</b>
                  <em>
                    {d.menorDia
                      ? `${diaDaSemana(d.menorDia.dia)} · ${un(d.menorDia.qtd)} (entre os dias com venda)`
                      : 'sem venda'}
                  </em>
                </div>
                <div className="gm-bloco">
                  <span>QUEM MAIS VENDEU</span>
                  <b>{d.vendedores[0]?.nome ?? '—'}</b>
                  <em>
                    {d.vendedores[0]
                      ? `${un(d.vendedores[0].qtd)} em ${fmt(d.vendedores[0].cupons, 'inteiro')} cupons`
                      : ''}
                  </em>
                </div>
                <div className="gm-bloco">
                  <span>NO PERÍODO</span>
                  <b>{un(d.unidades)}</b>
                  <em>
                    vendeu em {d.diasComVenda} dias · {d.diasAcima} acima de {d.acimaDe}
                  </em>
                </div>
              </div>

              <h3>Quem vendeu</h3>
              <ul className="gm-barras">
                {d.vendedores.map((v) => (
                  <li key={v.nome}>
                    <span className="gm-barra-nome">{v.nome}</span>
                    <span className="gm-barra">
                      <i style={{ width: `${String((v.qtd / maxVend) * 100)}%` }} />
                    </span>
                    <b>{fmt(v.qtd, 'inteiro')}</b>
                    <em className="cv-cinza cv-peq">{fmt(v.cupons, 'inteiro')} cup.</em>
                  </li>
                ))}
              </ul>

              <h3>Dia a dia</h3>
              <ul className="gm-barras">
                {d.dias.map((x) => (
                  <li key={x.dia} className={x.qtd > d.acimaDe ? 'gm-acima-linha' : ''}>
                    <span className="gm-barra-nome">
                      {dataBr(x.dia)} <em className="cv-cinza cv-peq">{diaDaSemana(x.dia)}</em>
                    </span>
                    <span className="gm-barra">
                      <i style={{ width: `${String((x.qtd / maxDia) * 100)}%` }} />
                    </span>
                    <b>{fmt(x.qtd, 'inteiro')}</b>
                    <em className="cv-cinza cv-peq">{fmt(x.cupons, 'inteiro')} cup.</em>
                  </li>
                ))}
              </ul>

              <h3>Cupons · {fmt(d.cupons.length, 'inteiro')}</h3>
              <table className="cv-tabela gm-tabela">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Cupom</th>
                    <th>Vendedor</th>
                    <th className="cv-dir">Qtd</th>
                  </tr>
                </thead>
                <tbody>
                  {d.cupons.map((c, k) => (
                    <tr key={`${c.saleId}-${String(k)}`}>
                      <td className="cv-mono">{diaCurto(c.dia)}</td>
                      <td>
                        <a className="cv-mono" href={`#/cupom?id=${c.saleId}`}>
                          {c.cupom}
                        </a>
                      </td>
                      <td>{c.vendedor ?? <span className="cv-cinza">sem vendedor</span>}</td>
                      <td className="cv-dir cv-forte">{fmt(c.qtd, 'inteiro')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
